import React from 'react';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';

import Chip from '@mui/material/Chip';

export const AttachmentList = ({ attachments = [] }) => {
  if (!attachments.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
      {attachments.map((attachment, index) => (
        <Box key={`${attachment.fileName || 'attachment'}-${index}`}>
          {attachment.mimeType?.startsWith('image/') && attachment.url && (
            <Box
              component="img"
              src={attachment.url}
              alt={attachment.fileName || 'Task photo'}
              sx={{
                width: 120,
                height: 80,
                objectFit: 'cover',
                borderRadius: 1,
                display: 'block',
                mb: 0.5
              }}
            />
          )}
          <Box
            component={attachment.url ? 'a' : 'div'}
            href={attachment.url || undefined}
            target={attachment.url ? '_blank' : undefined}
            rel={attachment.url ? 'noreferrer' : undefined}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              width: 'fit-content',
              color: attachment.url ? 'primary.main' : 'text.secondary',
              textDecoration: 'none',
              '&:hover': attachment.url ? { textDecoration: 'underline' } : undefined
            }}
          >
            <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">{attachment.fileName || 'Attachment'}</Typography>
          </Box>
          {attachment.sharepoint?.webUrl && (
            <Typography
              component="a"
              href={attachment.sharepoint.webUrl}
              target="_blank"
              rel="noreferrer"
              variant="caption"
              sx={{ display: 'block', width: 'fit-content', mt: 0.25, color: 'primary.main' }}
            >
              Open in SharePoint
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

AttachmentList.propTypes = {
  attachments: PropTypes.any
};

export const DetailItem = ({ icon, label, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '22px 110px minmax(0, 1fr)',
      gap: 1,
      alignItems: 'center'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        color: 'text.secondary',
        '& svg': { fontSize: 17 }
      }}
    >
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>
      {React.isValidElement(children) ? (
        children
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}
          style={{ fontWeight: '500' }}
        >
          {children || '—'}
        </Typography>
      )}
    </Box>
  </Box>
);

DetailItem.propTypes = {
  icon: PropTypes.any,
  label: PropTypes.any,
  children: PropTypes.any
};

export const PersonAvatar = ({ name, showName = true }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Avatar
      sx={{
        width: 26,
        height: 26,
        fontSize: 12,
        bgcolor: '#dbe9ef',
        color: '#24566a'
      }}
    >
      {(name || '?')
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()}
    </Avatar>
    {showName && (
      <Typography variant="body2" style={{ fontWeight: '500' }}>
        {name || '—'}
      </Typography>
    )}
  </Box>
);

PersonAvatar.propTypes = {
  name: PropTypes.any,
  showName: PropTypes.any
};

export const StatusIcon = ({ status, statusStyle }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Chip
      label={status}
      size="small"
      sx={{
        height: 24,
        borderRadius: 1,
        fontWeight: 700,
        ...statusStyle
      }}
    />
  </Box>
);

StatusIcon.propTypes = {
  status: PropTypes.any,
  statusStyle: PropTypes.any
};
